import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api";
import { FaUserTie, FaEuroSign, FaFileInvoiceDollar, FaChartLine, FaTrophy } from "react-icons/fa";

const euro = (v) =>
  (Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

const fmtDate = (val) => {
  if (!val) return "—";
  const iso = val.includes("T") ? val : val.replace(" ", "T") + "Z";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return val;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// Fiche d'un commercial : identité + stats + détail dépliable des commissions.
function CommercialCard({ c, rank }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const avg = c.count > 0 ? c.total / c.count : 0;
  const initials = (c.username || "?").slice(0, 2).toUpperCase();

  return (
    <div className="bg-base-100 rounded-2xl border border-base-200 shadow-sm overflow-hidden card-lift">
      {/* En-tête fiche */}
      <div className="p-5 flex items-center gap-4 border-b border-base-200">
        <div className="relative">
          <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-extrabold text-lg">
            {initials}
          </div>
          {rank === 0 && (
            <span className="absolute -top-2 -right-2 text-amber-500" title={t("performance.top")}>
              <FaTrophy size={16} />
            </span>
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FaUserTie className="text-base-content/40 shrink-0" size={13} />
            <h3 className="font-bold text-base-content truncate">{c.username}</h3>
          </div>
          <p className="text-xs text-base-content/40">{t("performance.role")}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 divide-x divide-base-200">
        <Stat icon={<FaEuroSign />} label={t("performance.commissions")} value={euro(c.total)} accent="text-emerald-600" />
        <Stat icon={<FaFileInvoiceDollar />} label={t("performance.validatedQuotes")} value={c.count} />
        <Stat icon={<FaChartLine />} label={t("performance.avgPerQuote")} value={euro(avg)} />
      </div>

      {/* Détail dépliable */}
      {c.quotes?.length > 0 && (
        <>
          <button
            onClick={() => setOpen((o) => !o)}
            className="w-full px-5 py-2.5 text-xs font-semibold text-base-content/50 hover:text-base-content hover:bg-base-200/50 transition-colors flex items-center justify-center gap-1 border-t border-base-200"
          >
            {open ? `${t("performance.hideDetail")} ▲` : `${t("performance.showDetail", { count: c.quotes.length })} ▼`}
          </button>
          {open && (
            <div className="overflow-x-auto border-t border-base-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-base-200/60 text-[10px] uppercase tracking-wider text-base-content/50">
                    <th className="py-2 px-4 text-left">{t("performance.colQuote")}</th>
                    <th className="py-2 px-4 text-left">{t("performance.colValidatedOn")}</th>
                    <th className="py-2 px-4 text-right">{t("performance.colHtRate")}</th>
                    <th className="py-2 px-4 text-right">{t("performance.colCommission")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-base-200">
                  {c.quotes.map((q) => (
                    <tr key={q.id} className="hover:bg-base-200/40">
                      <td className="py-1.5 px-4 text-base-content/70">#{q.id} · {q.client_name}</td>
                      <td className="py-1.5 px-4 text-base-content/50">{fmtDate(q.validated_at)}</td>
                      <td className="py-1.5 px-4 text-right text-base-content/60">{euro(q.commissionBase ?? q.totalHT)} × {q.rate}%</td>
                      <td className="py-1.5 px-4 text-right font-semibold text-emerald-600">{euro(q.commission)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ icon, label, value, accent = "text-base-content" }) {
  return (
    <div className="p-4 text-center">
      <div className="flex items-center justify-center gap-1 text-base-content/40 text-[11px] font-semibold uppercase tracking-wide">
        {icon} {label}
      </div>
      <div className={`mt-1 text-lg font-extrabold ${accent}`}>{value}</div>
    </div>
  );
}

export default function PerformancePage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get("/users/commissions");
        if (mounted) setData(res.data);
      } catch {
        if (mounted) setData({ grandTotal: 0, commercials: [] });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Filtre par mois (YYYY-MM) sur la date de validation. Vide = tout l'historique.
  const [month, setMonth] = useState("");

  // Recalcule les totaux par commercial sur le mois filtré, puis classe par commission.
  const { commercials, grandTotal } = useMemo(() => {
    const src = data?.commercials || [];
    const filtered = src
      .map((c) => {
        const quotes = (c.quotes || []).filter(
          (q) => !month || String(q.validated_at || "").slice(0, 7) === month
        );
        const total = Number(quotes.reduce((s, q) => s + (q.commission || 0), 0).toFixed(2));
        return { ...c, quotes, total, count: quotes.length };
      })
      .filter((c) => !month || c.count > 0)
      .sort((a, b) => (b.total || 0) - (a.total || 0));
    const gt = Number(filtered.reduce((s, c) => s + (c.total || 0), 0).toFixed(2));
    return { commercials: filtered, grandTotal: month ? gt : (data?.grandTotal ?? gt) };
  }, [data, month]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <FaChartLine size={18} />
          </span>
          <div>
            <h1 className="text-xl font-extrabold text-base-content">{t("performance.title")}</h1>
            <p className="text-sm text-base-content/50">{t("performance.count", { count: commercials.length })}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-base-content/50">
              {t("performance.monthFilter", "Mois")}
            </label>
            <div className="flex items-center gap-1">
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="input input-bordered input-sm bg-base-200 border-base-300 focus:border-primary focus:outline-none"
              />
              {month && (
                <button
                  onClick={() => setMonth("")}
                  className="btn btn-ghost btn-sm"
                  title={t("performance.allMonths", "Tout")}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl px-5 py-3 text-right">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">{t("performance.totalCommissions")}</div>
            <div className="text-2xl font-extrabold text-emerald-700">{euro(grandTotal)}</div>
          </div>
        </div>
      </div>

      {/* Fiches */}
      {commercials.length === 0 ? (
        <div className="text-center text-base-content/40 text-sm py-16">
          {t("performance.empty")}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {commercials.map((c, i) => (
            <CommercialCard key={c.user_id} c={c} rank={i} />
          ))}
        </div>
      )}
    </div>
  );
}
